import { Component, inject, OnDestroy, OnInit, AfterViewInit, signal, computed, ChangeDetectionStrategy } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { environment } from 'src/environments/environment';
import { Store } from '@ngxs/store';
import { filter, Observable, Subject, takeUntil, tap } from 'rxjs';
import { RouterLink, RouterLinkActive, Router, NavigationEnd } from '@angular/router';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { AsyncPipe } from '@angular/common';
import { MatTooltip } from '@angular/material/tooltip';
import { homeMenuTiles } from '../../configs/homeMenu.config';
import { INavigationItem } from '../../interfaces/INavigationItem';
import { IUser } from '../../interfaces/IUser';
import { USER_ROLES } from '../../enums/UserRoles.enum';
import { UserState } from '../../states/user.state';
import { NavigationOperations } from '../../operations/navigation.operations';
import { UserComponent } from '../user/user.component';
import { SidebarTab } from '../../services/sidebar.service';
import { NotificationsState } from '../../states/notifications/notifications.state';
import { INotification } from '../../interfaces/notifications/INotification';
import { HeaderOperationsService } from './header.operations';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [RouterLink, MatMenuTrigger, MatMenu, MatMenuItem, UserComponent, RouterLinkActive, TranslatePipe, AsyncPipe, MatTooltip],
})
export class HeaderComponent implements OnInit, AfterViewInit, OnDestroy {
  private store = inject(Store);
  private headerOperations = inject(HeaderOperationsService);
  private router = inject(Router);
  private translateService = inject(TranslateService);

  protected userRole$: Observable<USER_ROLES> = this.store.select<USER_ROLES>(UserState.userRole);
  private user$: Observable<IUser> = this.store.select<IUser>(UserState.user);
  private readonly pendingNotifications = this.store.selectSignal<INotification[]>(NotificationsState.pendingNotifications);
  protected readonly notificationCount = computed(() => this.pendingNotifications().length);
  public isSidebarOpen = false;
  public navItems: INavigationItem[] = homeMenuTiles;
  public logoImg = 'assets/images/sag-logo/sie-logo-petrol-rgb.svg';
  public remainingTimeMessage: string;
  public theme: 'dark' | 'light' = this.headerOperations.getCurrentTheme();
  public language: string = this.headerOperations.getCurrentLanguage();
  public currentPageTitle = '';
  protected readonly USER_ROLES = USER_ROLES;

  // Mobile responsiveness signal
  protected readonly isMobile = signal(window.innerWidth <= 800);
  private resizeListener: (() => void) | undefined;

  public readonly reportingLink = environment.reporting;
  public readonly serviceDeskLink = environment.serviceDeskLink;
  private destroy$: Subject<boolean> = new Subject<boolean>();

  ngOnInit(): void {
    this.loadNavigationItems();
    this.setTheme(this.theme ? this.theme : 'light');
    this.changeLang(this.language ? this.language : 'en');
    this.setupRouteChangeListener();
    this.updateCurrentPageTitle();
  }

  ngAfterViewInit(): void {
    window.addEventListener('resize', this.updateSidebarState.bind(this));
    this.resizeListener = () => window.removeEventListener('resize', this.updateSidebarState.bind(this));
  }

  ngOnDestroy(): void {
    if (this.resizeListener) {
      this.resizeListener();
    }
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  public changeLang(langCode: string): void {
    this.headerOperations.changeLang(langCode);
    this.language = langCode;
    setTimeout(() => this.updateCurrentPageTitle(), 100);
  }

  public setTheme(mode: 'dark' | 'light'): void {
    this.headerOperations.setTheme(mode);
    this.theme = mode;
  }

  public openApplicationFilterDialog(): void {
    this.headerOperations.openApplicationFilterDialog();
  }

  protected openCreateNewsDialog(): void {
    this.headerOperations.openCreateNewsDialog();
  }

  protected openNewsSidebar(): void {
    this.headerOperations.openSidebar(SidebarTab.News);
  }

  protected openNotificationsSidebar(): void {
    this.headerOperations.openSidebar(SidebarTab.Notifications);
  }

  public openTimesheetSettingsDialog(): void {
    this.headerOperations.openTimesheetSettingsDialog();
  }

  protected openEditImpersonationDialog(): void {
    this.headerOperations.openEditImpersonationDialog();
  }

  protected openSelectImpersonationDialog(): void {
    this.headerOperations.openSelectImpersonationDialog();
  }

  protected stopImpersonation() {
    this.headerOperations.stopImpersonation();
  }

  protected navigateToPersonalInformationPage(): void {
    this.headerOperations.navigateToPersonalInformationPage();
  }

  protected navigateToReleaseNotesPage(): void {
    this.headerOperations.navigateToReleaseNotesPage();
  }

  protected isImpersonating(): boolean {
    return this.headerOperations.isImpersonating();
  }

  protected toggleSidebar(): void {
    return this.headerOperations.openSidebar(SidebarTab.Navigation);
  }

  private setupRouteChangeListener(): void {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        tap(() => this.updateCurrentPageTitle()),
        tap(() => this.headerOperations.openDialogFromUrl()),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  private updateCurrentPageTitle(): void {
    const currentUrl = this.router.url;
    const matchingNavItem = this.navItems.find(item => currentUrl.startsWith(item.route));

    if (matchingNavItem) {
      // Use the translation key to get the human-readable title
      this.currentPageTitle = this.translateService.instant(matchingNavItem.transKey) as string;
    } else {
      // Handle special cases for routes not in navigation
      this.currentPageTitle = this.getPageTitleFromRoute(currentUrl);
    }
  }

  private getPageTitleFromRoute(url: string): string {
    // Map specific routes to their translation keys
    const routeMap: { [key: string]: string } = {
      '/home': 'navigation.home',
      '/': 'navigation.home',
      '/personal-information': 'header.personalInformation',
      '/release-notes': 'header.releaseNotes',
      '/how-to': 'navigation.howTo',
    };

    for (const route in routeMap) {
      if (url.startsWith(route)) {
        return this.translateService.instant(routeMap[route]) as string;
      }
    }

    // Default fallback - try to extract a readable name from the URL
    const segments = url.split('/').filter(segment => segment);
    if (segments.length > 0) {
      const lastSegment = segments[segments.length - 1];
      // Convert kebab-case to title case
      return lastSegment
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }

    return 'Home';
  }

  private loadNavigationItems(): void {
    this.user$
      .pipe(
        filter((user: IUser) => !!user.role),
        tap((user: IUser) => (this.navItems = NavigationOperations.filterByUser(homeMenuTiles, user))),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  private updateSidebarState(): void {
    const newIsMobile = window.innerWidth <= 800;
    this.isMobile.set(newIsMobile);
    this.headerOperations.setIsMobile(newIsMobile);
  }
}
